import { memo, useEffect, useState } from 'react';

import { AreaChartWrapper } from 'PDF/components/Charts/AreaChart/AreaChartWrapper';
import { ChartWithBarsWrapper } from 'PDF/components/Charts/ChartWithBars';
import { ChartWithStackedBarsWrapper } from 'PDF/components/Charts/ChartWithStackedBars';
import { IImageChart } from 'PDF/components/Charts/types';
import { SalesFunnel } from 'PDF/components/SalesFunnel/SalesFunnel';
import { PDF_SECTIONS } from 'PDF/constants/sections';
import { prepareSectionId } from 'PDF/helpers/pdfMapping';
import { useGenerateImageFromElement } from 'PDF/hooks/useGenerateImageFromElement';
import { IPdfPageProps } from 'PDF/types/pdf';
import { useBusinessToolStore } from 'store/useBusinessToolStore';
import { getBusinessPlanChapters, getBusinessPlanType } from 'store/useBusinessToolStore/selectors';

import { useExamplePreview } from 'hooks/useExamplePreview';

import {
  getMarketingAndSalesProps,
  getRatioInMonetaryChartData,
  getSalesPlanInMonetaryChartData,
  getSalesPlanTableData,
  getTotalValuesInMonetaryChartData,
  getValuesInPhysicalChartData,
} from './mapping';
import { IMarketingAndSalesData } from './MarketingAndSalesPdf';

interface IMarketingAndSalesProps extends IPdfPageProps {
  isExamplePage: boolean
}

export const MarketingAndSales = memo(({ saveSectionProps, sectionPicture, isExamplePage }: IMarketingAndSalesProps) => {
  const businessPlanChapters = useBusinessToolStore(getBusinessPlanChapters);
  const businessPlanType = useBusinessToolStore(getBusinessPlanType);


  const { magazineTexts, salesChannelsData, salesFunnelData } = getMarketingAndSalesProps(
    businessPlanChapters,
    businessPlanType,
    isExamplePage
  );

  const [firstChartData, secondChartData] = getSalesPlanInMonetaryChartData(businessPlanChapters, isExamplePage);
  const salesPlanInMonetaryRatioChartData = getRatioInMonetaryChartData(businessPlanChapters, isExamplePage);
  const salesPlanInPhysicalChartData = getValuesInPhysicalChartData(businessPlanChapters, isExamplePage);
  const totalValuesInMonetaryChartData = getTotalValuesInMonetaryChartData(businessPlanChapters, isExamplePage);

  const salesPlanTableData = getSalesPlanTableData(businessPlanChapters, isExamplePage);

  const [portal, imageSrc] = useGenerateImageFromElement(<SalesFunnel data={salesFunnelData} />);

  const [firstMonetaryChartImage, setFirstMonetaryChartImg] = useState<IImageChart>({
    src: '',
    title: '',
    shouldGenerate: firstChartData.data.length > 0,
  });
  const [secondMonetaryChartImage, setSecondMonetaryChartImg] = useState<IImageChart>({
    src: '',
    title: '',
    shouldGenerate: secondChartData.data.length > 0,
  });
  const [physicalValuesChartImage, setPhysicalValuesChartImage] = useState<IImageChart>({
    src: '',
    title: '',
    shouldGenerate: salesPlanInPhysicalChartData.data.length > 0,
  });
  const [monetaryRatioChartImage, setMonetaryRatioChartImg] = useState<IImageChart>({
    src: '',
    title: '',
    shouldGenerate: salesPlanInMonetaryRatioChartData.datasets.length > 0,
  });
  const [monetaryTotalValuesChartImage, setMonetaryTotalValuesChartImg] = useState<IImageChart>({
    src: '',
    title: '',
    shouldGenerate: totalValuesInMonetaryChartData.data.length > 0,
  });

  const updateFirstMonetaryChartImage = (title: string, src: string) => {
    setFirstMonetaryChartImg({ title, src });
  };

  const updatePhysicalValuesChartImage = (title: string, src: string) => {
    setPhysicalValuesChartImage({ title, src });
  };

  const updateSecondMonetaryChartImage = (title: string, src: string) => {
    setSecondMonetaryChartImg({ title, src });
  };

  const updateMonetaryRatioChartImg = (title: string, src: string) => {
    setMonetaryRatioChartImg({ title, src });
  };

  const updateMonetaryTotalValuesImage = (title: string, src: string) => {
    setMonetaryTotalValuesChartImg({ title, src });
  };

  const data: IMarketingAndSalesData = {
    firstMonetaryChartImageSrc: firstMonetaryChartImage.src,
    secondMonetaryChartImageSrc: secondMonetaryChartImage.src,
    monetaryRatioChartImageSrc: monetaryRatioChartImage.src,
    monetaryTotalValuesChartImageSrc: monetaryTotalValuesChartImage.src,
    physicalValuesChartImageSrc: physicalValuesChartImage.src,
    salesFunnelImageSrc: imageSrc,
    salesFunnelData,
    salesChannelsData,
    salesPlanTableData,
  };

  useEffect(() => {
    const allChartImagesLoaded = [
      firstMonetaryChartImage,
      secondMonetaryChartImage,
      physicalValuesChartImage,
      monetaryTotalValuesChartImage,
      monetaryRatioChartImage,
    ]
      .filter((image) => image.shouldGenerate)
      .every((image) => image.src);

    if (imageSrc && allChartImagesLoaded && sectionPicture) {
      saveSectionProps(prepareSectionId(businessPlanType, PDF_SECTIONS.MARKETING_AND_SALES), {
        magazineItems: magazineTexts,
        data,
      });
    }
  }, [
    firstMonetaryChartImage,
    imageSrc,
    magazineTexts,
    monetaryRatioChartImage,
    monetaryTotalValuesChartImage,
    physicalValuesChartImage,
    secondMonetaryChartImage,
    sectionPicture,
  ]);

  return (
    <>
      {portal}
      {firstMonetaryChartImage.shouldGenerate && (
        <ChartWithBarsWrapper
          aspectRatio={2.5}
          info={firstChartData}
          withDataLabeInsideBar
          updateState={updateFirstMonetaryChartImage}
        />
      )}
      {secondMonetaryChartImage.shouldGenerate && (
        <ChartWithBarsWrapper
          aspectRatio={2.5}
          info={secondChartData}
          withDataLabeInsideBar
          updateState={updateSecondMonetaryChartImage}
        />
      )}
      {physicalValuesChartImage.shouldGenerate && (
        <AreaChartWrapper
          aspectRatio={2.5}
          info={salesPlanInPhysicalChartData}
          updateState={updatePhysicalValuesChartImage}
          stacked
        />
      )}
      {monetaryRatioChartImage.shouldGenerate && (
        <ChartWithStackedBarsWrapper
          aspectRatio={2.5}
          info={salesPlanInMonetaryRatioChartData}
          updateState={updateMonetaryRatioChartImg}
          axiosInPercentageProps={{ min: 0, max: 100, stepSize: 20 }}
          withBarInsideLabels
        />
      )}
      {monetaryTotalValuesChartImage.shouldGenerate && (
        <AreaChartWrapper
          aspectRatio={2.5}
          info={totalValuesInMonetaryChartData}
          updateState={updateMonetaryTotalValuesImage}
          showDataLabels
        />
      )}
    </>
  );
});
